import React from "react"
import { useTranslation } from "react-i18next"
import type { IPrintAgentOverviewDto } from "../../../../Objects/PrintAutomation/IPrintAgentOverviewDto"
import type { IPrinterConfigurationDto } from "../../../../Objects/PrintAutomation/PrintAutomation"
import type { EPrintType } from "../../../../Enum/EPrintType"
import { PrinterConfigCard } from "./PrinterConfigCard"
import { ButtonIcon } from "../../../../components/Button/Button"
import { faTrash } from "@fortawesome/free-solid-svg-icons"
import Animate, { AnimationTypes } from "../../../../components/Animate/Animate"

type Props = {
  agent: IPrintAgentOverviewDto
  pairingStatus: string
  onDelete: (agentId: number) => void
  onChangeType: (printer: IPrinterConfigurationDto, printType: EPrintType) => void
  onSaveType: (printer: IPrinterConfigurationDto, printType: EPrintType) => void
  onOpenSettings: (printer: IPrinterConfigurationDto) => void
  onTestPrint: (printerId: number) => void
}

export const PrintAgentCard: React.FC<Props> = ({
  agent,
  pairingStatus,
  onDelete,
  onChangeType,
  onSaveType,
  onOpenSettings,
  onTestPrint,
}) => {
  const { t } = useTranslation(["pages"])

  return (
    <Animate type={AnimationTypes.fadeInTraslate} duration="1.2s">
      <article className="rounded-lg border-2 border-orange-200 bg-white p-3 shadow-sm">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2">
            <strong className="text-base text-slate-800">{agent.name}</strong>
            <span
              className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-bold ${
                agent.isOnline
                  ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                  : "border-red-200 bg-red-50 text-red-700"
              }`}
            >
              {agent.isOnline ? t("label_print_automation_online") : t("label_print_automation_offline")}
            </span>
          </div>

          <ButtonIcon
            icon={faTrash}
            sizeNumber={34}
            bgColor="#ef4444"
            iconColor="white"
            onClick={() => onDelete(agent.id)}
            descriptionKey="interactionDescriptions:pages.myRestaurant.printAutomation.components.printAgentCard.button.onDeleteAgentId"
          />
        </div>

        <Animate type={AnimationTypes.fadeInTraslate} duration="1.4s">
          <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-4">
            <div className="rounded-lg border border-orange-100 bg-white p-2 text-xs text-slate-900">
              <strong>{t("label_print_automation_agent_id_label")}:</strong> {agent.id}
            </div>

            <div className="rounded-lg border border-orange-100 bg-white p-2 text-xs text-slate-700">
              {t("label_print_automation_machine", { machineId: agent.machineId })}
            </div>

            <div className="rounded-lg border border-orange-100 bg-white p-2 text-xs text-slate-900">
              <strong>
                {t("label_print_automation_last_seen", { date: agent.lastSeenAt?.toReadableDate() ?? "-" })}
              </strong>
            </div>

            <div className="rounded-lg border border-orange-100 bg-white p-2 text-xs text-slate-700">
              <strong>{t("label_print_automation_pairing_status")}:</strong> {pairingStatus}
            </div>
          </div>
        </Animate>

        <div className="mt-3 grid gap-3">
          {agent.printers.map((printer, index) => (
            <Animate key={index} type={AnimationTypes.fadeInTraslate} duration={1.5 + index * 0.5 + "s"}>
              <PrinterConfigCard
                key={printer.id ?? `${agent.id}-${printer.printerName}`}
                printer={printer}
                onChangeType={onChangeType}
                onSaveType={onSaveType}
                onOpenSettings={onOpenSettings}
                onTestPrint={onTestPrint}
              />
            </Animate>
          ))}
        </div>
      </article>
    </Animate>
  )
}
